import { ChangeEvent, MouseEvent, ReactElement, useEffect, useId, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useBreadcrumbs } from '@client/breadcrumbs/hooks';
import { RemoteRecordHandler } from '@client/common/components/RemoteRecordHandler';
import { debounce } from '@client/common/utils/debounce';
import { isServer } from '@client/common/utils/env';
import { parse, stringify } from '@client/common/utils/queryParams';
import { META_TAGS } from '@client/common/utils/seo/metaTags';
import { config } from '@client/config';
import { MetaTagsType } from '@client/content/components/ModularPage';
import { useTranslate } from '@client/i18n/hooks';
import { useGenerateManufacturerUrl, useManufacturersEffect } from '@client/manufacturer/hooks';
import { useRouterPush } from '@client/routes/hooks';
import { ListPage } from './ListPage';

export const QUERY = 'q';
const FILTER = 'filter';
const SEARCH_INPUT_DEBOUNCE = 350;

export const ListPageContainer = (): ReactElement => {
  const t = useTranslate();
  const id = useId();
  const { pathname } = useLocation();
  const searchParams = !isServer ? parse() : {};
  const filter = searchParams[FILTER];
  const query = searchParams[QUERY];
  const queryKey = useRef(query);
  const push = useRouterPush();
  const manufacturersData = useManufacturersEffect(query, filter);
  const generateManufacturerUrl = useGenerateManufacturerUrl();

  useBreadcrumbs([]);

  const { isLoaded } = manufacturersData;
  const isManufacturersDataSpecified = !!manufacturersData;
  const shopName = config.shopName;
  const title = t('All manufacturers');
  const ogUrl = isServer
    ? `${config.url.root}${pathname}`
    : window.location.href;

  useEffect((): void => {
    if (!query && filter) {
      queryKey.current = id;
    }
  }, [query, filter]);

  const handleFilteringElementClick = (element: string) => (e: MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    push({ search: stringify({ [FILTER]: element }) });
  };

  const handleClick = (url: string) => (e: MouseEvent<any>): void => {
    e.preventDefault();
    push(url);
  };

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateUrlDebounced(event.currentTarget.value);
  };

  const updateUrlDebounced = debounce(
    (value: string) => {
      push({ search: stringify({ [QUERY]: value }) });
    },
    SEARCH_INPUT_DEBOUNCE,
  );

  const metaTags: MetaTagsType = (!isLoaded || !isManufacturersDataSpecified)
    ? []
    : [
      {
        name: META_TAGS.ogTitle,
        content: title,
      },
      {
        name: META_TAGS.ogType,
        content: 'manufacturers',
      },
      {
        name: META_TAGS.ogUrl,
        content: ogUrl,
      },
      {
        name: META_TAGS.ogSitename,
        content: shopName,
      },
      {
        name: META_TAGS.title,
        content: title,
      },
      {
        name: META_TAGS.twitterCard,
        content: 'summary_large_image',
      },
      {
        name: META_TAGS.twitterTitle,
        content: title,
      },
    ];

  return (
    <RemoteRecordHandler remoteRecord={manufacturersData}>
      <ListPage
        filter={query ? '' : decodeURIComponent(filter || '')}
        generateManufacturerUrl={generateManufacturerUrl}
        manufacturers={manufacturersData}
        metaTags={metaTags}
        onFilteringElementClick={handleFilteringElementClick}
        onManufacturerNameClick={handleClick}
        onSearchInputChange={handleSearchInputChange}
        query={query}
        queryKey={queryKey.current}
      />
    </RemoteRecordHandler>
  );
};
